import * as React from "react";
import { SlidersHorizontal } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface FilterSheetProps {
  /** Filter controls — rendered inline on desktop, inside a bottom sheet on mobile. */
  children: React.ReactNode;
  /** Optional badge count of active filters (mobile trigger). */
  activeCount?: number;
  triggerLabel?: string;
  title?: string;
  description?: string;
  className?: string;
}

/**
 * FilterSheet — on desktop renders children inline. On mobile collapses them
 * behind a single "Filters" button that opens a bottom Sheet for a native feel.
 */
export function FilterSheet({
  children,
  activeCount = 0,
  triggerLabel = "Filters",
  title = "Filters",
  description = "Refine the list",
  className,
}: FilterSheetProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);

  if (!isMobile) {
    return <div className={cn("flex flex-wrap items-center gap-2", className)}>{children}</div>;
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="relative">
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          {triggerLabel}
          {activeCount > 0 ? (
            <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-medium text-primary-foreground">
              {activeCount}
            </span>
          ) : null}
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[85svh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 flex flex-col gap-3">{children}</div>
      </SheetContent>
    </Sheet>
  );
}

export default FilterSheet;
